import * as d3 from 'd3';
import { Legend } from '../component/legend';
import { Swatches } from '../component/swatches';
import { colorScheme } from '../d3/color-interpolator';
import {Datum, SpatialCoordinate, SpatialBounds, TypedDatumDefinition } from '../data-source/types';
import { Color } from '../svg/svg';
import { ColorInterpolator, ColorMapEntry, colorToString, constraintColorInterpolator, extractMonoHueInterpolator, getColorScheme, getCustomColorInterpolator, getDefaultColorInterpolator, modifyDomain, paletteEntries } from '../util/color';
import { ModellingGrid } from '@mapp/r-mapp-lib';

//const baseColorInterpolator = getDefaultColorInterpolator(0);

const baseColorInterpolator =  constraintColorInterpolator(getCustomColorInterpolator(paletteEntries));
const impactColorInterpolator = constraintColorInterpolator(
	modifyDomain(getCustomColorInterpolator(paletteEntries), [0,1],[0.23,0.02]), 
	false,//noAlpha = false
	 false,//hideUnderflow = true,
	 false,// hideOverflow = false, 
	false,//  normalizedInput = false
);
const impactColorScheme = getColorScheme(impactColorInterpolator, [0.1,1.0], 10);


export interface ImpactParameters {
	thresholds: number[],
	minValue: number,
	maxValue: number,
	scalarWeight: number,
	sizeWeight: number
}
export interface ProjectTypeImpactParameters extends ImpactParameters{
	step: { x:number, y:number }
}
export interface GridParameters {
	proj: string,//when the proj key inside the proj string has not been defined, the ?orig and/or ?cent parameters are in geodetic form
	region: string, //sort of label
	key: string;
	dx: number,
	dy: number,
	nx: number,
	ny: number,
	timestep: number, //inseconds
	unit: string// isq reference
	unitLabel: string //human readable unit
	xorig: number, //either ?orig or ?cent has been defined 
	yorig: number,
	xcent: number,
	ycent: number,
	supportsWindFields: boolean,
}
interface ModelingConfiguration {
	maptimeline: {
		enabled: boolean,
		combine: 'none' | 'category' | 'all',
		render: 'line' | 'band' | 'line_band',
		color: 'palette' | 'category'
	},
	source: {
		enabled: boolean
	},
	monitors:{
		enabled:boolean
	}
}
export interface ModelingTypeDefinition extends TypedDatumDefinition {
	template?: {
		parameters: {
			render: {
				thresholds: number[]
			}
		}
	},
	//id,label,icon
	driver: string,
	hpc: string,
	model: string,
	autoStart?: boolean,
	configuration?:ModelingConfiguration,
	grid: GridParameters,
	impact: ProjectTypeImpactParameters
	output?: {
		wind2d?: boolean
	}
	modellingGrid: ModellingGrid;
}

export interface Geometry extends SpatialCoordinate {
	label:string,
	id:string
}
export interface SinglePointGeometry extends Geometry {
	lat:number,
	lng:number
}
export interface MultiPointGeometry extends Geometry {
	coordinates:{ lat:number, lng:number}[]
}

export interface ModelRun{
	runId: string, 
	version: number,
	status: string, 
	range?: { 
		min: number, 
		max: number 
		}, 
	geometry: SinglePointGeometry | MultiPointGeometry
}

export interface ModelProjectInterval extends ModelRun{
	start: number,
	end: number,
}

export interface ModelProject extends Datum{
	type:string,
	start: number,
	end: number,
	stop: number,
	interval:number,
	parameters:any,
	producers:string[],
	profile: ModelProjectInterval[]
}

export interface ModelRunProfile {
    start: number,
    end: number,
    properties: {
        runs: { [key:string]: ModelRun }
    }
}

export const geometry2SpatialCoordinate = ( geometry: Geometry) : (SpatialCoordinate & Geometry) => {
	if( (<MultiPointGeometry>geometry).coordinates ){
		let multiPointGeometry = <MultiPointGeometry>geometry;
		let bounds = multiPointGeometry.coordinates.reduce( (bounds, coordinate) => {
			bounds.north = bounds.north === undefined ? coordinate.lat : Math.max(coordinate.lat,bounds.north);
			bounds.south = bounds.south === undefined ? coordinate.lat : Math.min(coordinate.lat,bounds.south);
			bounds.east = bounds.east === undefined ? coordinate.lng : Math.max(coordinate.lng,bounds.east);
			bounds.west = bounds.west === undefined ? coordinate.lng : Math.min(coordinate.lng,bounds.west);
			return bounds;
		}, <SpatialBounds>{ });
		return Object.assign( bounds, multiPointGeometry);
	} else {
		return <SinglePointGeometry>geometry;
	}
}

export const getCategoriesLegend = (categoryLabels: string[], categoryColors: Color[] ): HTMLElement => {
//	return Swatches( d3.scaleOrdinal(categoryLabels, categoryColors.map( c => colorToString(c))), { columns: "150px" });	
	return Swatches( d3.scaleOrdinal(categoryLabels, categoryColors.map( c => colorToString(c))), { });	
}
export const getScalarFieldLegend = (title: string, palette: ColorMapEntry[],  thresholds: number[], hue?: number): SVGSVGElement => {
	const 	colorInterpolator = hue === undefined ? getCustomColorInterpolator(palette) :  getModelingMonoHueInterpolator(palette, hue );
	const cssColorScheme = colorScheme([0].concat(thresholds), colorInterpolator, true);
	return Legend( d3.scaleThreshold(thresholds, cssColorScheme), {title: title });
}
export const getImpactScoreLegend = (title: string): SVGSVGElement => {
	return Legend(d3.scaleOrdinal(["1","2","3","4","5","6","7","8","9","10"], impactColorScheme.map( c => colorToString(c)) ), {title: title }) ;
}
export const getImpactScoreColor = (score: number): Color => {
	return impactColorInterpolator(score);
}
export const getModelingMonoHueInterpolator = (palette: ColorMapEntry[], hue: number): ColorInterpolator =>{
	return extractMonoHueInterpolator(palette, hue, 0.3);
}
export const getCategoryColor = (hue: number): Color =>{
	return baseColorInterpolator(hue);
}